import LogoutIcon from '@mui/icons-material/Logout';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';

import { CopyButton } from '#components/copy-button/copy-button.js';
import { UserAvatar } from '#components/user-avatar/user-avatar.js';

import type { User } from '@auth0/auth0-spa-js';

export type UserControlLoggedInProps = {
	user: User | undefined;
};

export function UserControlLoggedIn({ user }: UserControlLoggedInProps) {
	const navigate = useNavigate();
	const posthog = usePostHog();

	// Stable, always-present support reference users can quote; on-call uses it to
	// open the PostHog session replay and grep backend logs by posthogSessionId.
	// Undefined when PostHog isn't initialized (dev).
	const supportReference = posthog.get_session_id();

	const [isMenuOpen, setIsMenuOpen] = useState<{ anchorEl: HTMLElement } | false>(false);

	// Route through the canonical /log-out flow so every logout entry point clears
	// the persistent query cache and session-scoped atoms before the Auth0 redirect.
	const handleLogOut = useCallback(() => {
		void navigate({ to: '/log-out' });
	}, [navigate]);

	return (
		<>
			<Button
				variant='text'
				startIcon={
					<UserAvatar src={user?.picture} user={user} alt={user?.name} title={user?.name} size='sm' className='mr-1' />
				}
				className='rounded p-2 text-gray-a11 italic'
				onClick={(event) => {
					setIsMenuOpen({ anchorEl: event.currentTarget });
				}}
			>
				{user ?
					<span>{user.name}</span>
				:	<Skeleton width={80} />}
			</Button>

			<Menu
				anchorEl={isMenuOpen === false ? undefined : isMenuOpen.anchorEl}
				open={isMenuOpen !== false}
				onClose={() => setIsMenuOpen(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				classes={{
					paper: 'mb-1 min-w-40',
				}}
			>
				<MenuItem>
					<span className='text-gray-11'>{user?.email}</span>
				</MenuItem>
				{supportReference && (
					<MenuItem disableRipple className='flex-col items-start gap-0.5'>
						<span className='text-xs text-gray-10'>Support reference</span>
						<span className='flex items-center gap-1'>
							<code className='font-mono text-xs text-gray-11 select-all'>{supportReference}</code>
							<CopyButton textToCopy={supportReference} />
						</span>
					</MenuItem>
				)}
				<MenuItem onClick={handleLogOut}>
					<ListItemIcon>
						<LogoutIcon />
					</ListItemIcon>
					<span>Log out</span>
				</MenuItem>
			</Menu>
		</>
	);
}
